import React, { useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Folder } from 'lucide-react';
import { cn } from '@/admin/lib/utils';
import type { PublicShareItem } from '../lib/types';
import {
  KindIcon,
  ShareFilenameLabel,
  formatFilenameForSelectOption,
} from './share-viewer-shared';

// ---------------------------------------------------------------------------
// Nest depth
// ---------------------------------------------------------------------------

const SHARE_FILE_NAV_INDENT_PX = 14;

function shareItemNestDepth(item: Pick<PublicShareItem, 'relative_path'>): number {
  const normalized = item.relative_path.replace(/\\/g, '/').replace(/^\/+/, '');
  if (!normalized) return 0;
  const segments = normalized.split('/').filter(s => s.length > 0);
  return Math.max(0, segments.length - 1);
}

// ---------------------------------------------------------------------------
// Desktop sidebar (lg+)
// ---------------------------------------------------------------------------

export function FileNav(props: {
  folderLabel: string;
  items: PublicShareItem[];
  selectedItemId: string;
  onSelectItem: (id: string) => void;
}): React.JSX.Element {
  const [listExpanded, setListExpanded] = useState(true);
  const listId = 'share-file-nav-items';

  const handleToggleList = () => {
    setListExpanded(prev => !prev);
  };

  return (
    <nav className="flex h-full flex-col overflow-y-auto" aria-label="Files">
      <div className="px-3 pb-1 pt-2">
        <button
          type="button"
          onClick={handleToggleList}
          aria-expanded={listExpanded}
          aria-controls={listId}
          className="flex w-full min-w-0 items-center gap-1.5 rounded-md py-0.5 text-left text-[13px] font-medium text-[#8a8a8a] outline-none transition-colors hover:text-[#6b6b6b] focus-visible:ring-1 focus-visible:ring-[#0077ff]"
        >
          <Folder className="size-[14px] shrink-0" strokeWidth={2} aria-hidden />
          <span className="min-w-0 flex-1 truncate">{props.folderLabel}</span>
          <ChevronRight
            className={cn(
              'size-4 shrink-0 transition-transform duration-200',
              listExpanded && 'rotate-90'
            )}
            strokeWidth={2}
            aria-hidden
          />
        </button>
      </div>

      <div
        id={listId}
        hidden={!listExpanded}
        className="flex flex-col px-1.5 pb-2"
      >
        {props.items.map(item => {
          const active = item.id === props.selectedItemId;
          const nestDepth = shareItemNestDepth(item);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => props.onSelectItem(item.id)}
              aria-label={item.display_name}
              style={{
                paddingLeft: `calc(0.375rem + ${nestDepth * SHARE_FILE_NAV_INDENT_PX}px)`,
              }}
              className={cn(
                'group flex w-full items-center gap-2 rounded-[5px] py-1 pr-1.5 text-left transition-colors',
                'outline-none focus-visible:ring-1 focus-visible:ring-[#0077ff]',
                active
                  ? 'bg-[#ebebeb] text-[#1a1a1a]'
                  : 'text-[#6b6b6b] hover:bg-[#f0f0f0] hover:text-[#1a1a1a]'
              )}
            >
              <KindIcon
                kind={item.kind}
                className={cn(
                  'size-[15px] shrink-0',
                  active ? 'text-[#1a1a1a]' : 'text-[#8a8a8a] group-hover:text-[#6b6b6b]'
                )}
              />
              <span className="min-w-0 flex-1">
                <ShareFilenameLabel
                  name={item.display_name}
                  textClassName={cn(
                    'text-[14px] leading-[1.4]',
                    active ? 'font-medium' : 'font-normal'
                  )}
                />
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Tablet horizontal strip (md–lg)
// ---------------------------------------------------------------------------

export function TabletFileStrip(props: {
  items: PublicShareItem[];
  selectedItemId: string;
  onSelectItem: (id: string) => void;
}): React.JSX.Element | null {
  if (props.items.length <= 1) return null;

  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className="hidden shrink-0 bg-[#fafafa] md:block lg:hidden"
      aria-label="Files"
      role="navigation"
    >
      <div
        ref={scrollRef}
        className="flex gap-1 overflow-x-auto px-3 py-2"
      >
        {props.items.map(item => {
          const active = item.id === props.selectedItemId;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => props.onSelectItem(item.id)}
              aria-label={item.display_name}
              className={cn(
                'group flex shrink-0 items-center gap-2 rounded-[5px] px-2 py-[6px] text-[14px] leading-[1.4] transition-colors',
                'outline-none focus-visible:ring-1 focus-visible:ring-[#0077ff]',
                active
                  ? 'bg-[#ebebeb] font-medium text-[#1a1a1a]'
                  : 'text-[#6b6b6b] hover:bg-[#f0f0f0] hover:text-[#1a1a1a]'
              )}
            >
              <KindIcon
                kind={item.kind}
                className={cn(
                  'size-[15px] shrink-0',
                  active ? 'text-[#1a1a1a]' : 'text-[#8a8a8a] group-hover:text-[#6b6b6b]'
                )}
              />
              <span className="max-w-[180px] min-w-0">
                <ShareFilenameLabel
                  name={item.display_name}
                  textClassName={cn(
                    active ? 'font-medium' : 'font-normal'
                  )}
                />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile native select (<md)
// ---------------------------------------------------------------------------

export function MobileFilePicker(props: {
  items: PublicShareItem[];
  selectedItem: PublicShareItem;
  onSelectItem: (id: string) => void;
}): React.JSX.Element | null {
  if (props.items.length <= 1) return null;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    props.onSelectItem(e.target.value);
  };

  return (
    <div className="flex items-center gap-2 bg-[#fafafa] px-4 py-2 md:hidden">
      <KindIcon kind={props.selectedItem.kind} className="size-[14px] shrink-0 text-[#8a8a8a]" />
      <select
        value={props.selectedItem.id}
        onChange={handleChange}
        className="min-w-0 flex-1 cursor-pointer appearance-none bg-transparent text-[14px] font-medium text-[#1a1a1a] outline-none"
        aria-label="Select file"
      >
        {props.items.map(item => (
          <option key={item.id} value={item.id}>
            {formatFilenameForSelectOption(item.display_name)}
          </option>
        ))}
      </select>
      <ChevronDown className="size-3.5 text-[#8a8a8a]" strokeWidth={2} />
    </div>
  );
}
